import { ImageSourcePropType } from 'react-native';
import { Category, CategoryKey, Service, services, tradeOf } from './services';
import { rescheduleCutoffPhrase } from './booking';

/**
 * Long-form content for the service sheet.
 *
 * Kept out of `services.ts` deliberately: the catalogue there is what the home
 * screen needs to draw a card, and it is already 52 entries long. This module
 * is what the sheet needs once one is opened.
 *
 * Content is written per category rather than per service. Authoring seven
 * sections for all 52 services by hand would be 364 blocks to keep in sync,
 * and the honest truth is that every deep clean shares a method - what varies
 * between two cleaning services is the name, price and duration, all of which
 * already live on the `Service`. A service can still override any section.
 *
 * Nothing here uses `Math.random`: review counts and names must not reshuffle
 * on every render, so anything that looks arbitrary is derived from the
 * service key instead.
 */

export type ServiceStep = { title: string; body: string };
export type Faq = { question: string; answer: string };
export type Review = {
  name: string;
  rating: number;
  when: string;
  body: string;
};

/** Star counts, 5 first down to 1. */
export type Breakdown = [number, number, number, number, number];

export type ServiceDetail = {
  banners: ImageSourcePropType[];
  steps: ServiceStep[];
  includes: string[];
  excludes: string[];
  tips: string[];
  faqs: Faq[];
  breakdown: Breakdown;
  reviews: Review[];
};

type Template = {
  steps: ServiceStep[];
  includes: string[];
  excludes: string[];
  tips: string[];
  faqs: Faq[];
};

// ---------------------------------------------------------------- templates

const ARRIVAL: ServiceStep = {
  title: 'A verified professional arrives',
  body: 'You get their name, photo and ID before the door opens, and live tracking on the way.',
};

const CLOSE: ServiceStep = {
  title: 'Checked, cleared and signed off',
  body: 'The area is left tidy, you approve the work, and the itemised receipt lands in your bookings.',
};

const COMMON_FAQS: Faq[] = [
  {
    question: 'Do I need to be home?',
    answer:
      'Someone over 18 should be present to let the professional in and approve the work at the end.',
  },
  {
    question: 'What if I need to reschedule?',
    /* A getter, so it reads the cutoff ops set when the FAQ is shown - this
       list is built at import, before the config has loaded. It said "4
       hours", which no setting ever enforced. */
    get answer() {
      return `Reschedule free of charge up to ${rescheduleCutoffPhrase()} before the slot, from Bookings.`;
    },
  },
  {
    question: 'Is the price on this page final?',
    answer:
      'Yes. The amount shown is what you pay. Anything extra is quoted and approved by you first.',
  },
];

const TEMPLATES: Record<CategoryKey, Template> = {
  cleaning: {
    steps: [
      ARRIVAL,
      {
        title: 'The room is surveyed and prepped',
        body: 'Surfaces are cleared, furniture moved where needed, and delicate items set aside.',
      },
      {
        title: 'The 48-point sequence runs',
        body: 'Skirting to switch plates, top down, so nothing already cleaned is dirtied again.',
      },
      CLOSE,
    ],
    includes: [
      'All materials, machines and cleaning agents',
      'Dusting, vacuuming and mopping throughout',
      'Kitchen and bathroom sanitisation',
      'Before and after photographs in your booking history',
    ],
    excludes: [
      'Moving heavy furniture or appliances',
      'Exterior windows above ground floor',
      'Clearing personal belongings or clutter',
      'Stain removal that needs specialist treatment',
    ],
    tips: [
      'Clear countertops beforehand so more time goes on cleaning than tidying.',
      'Keep pets in another room - vacuums and unfamiliar people unsettle them.',
      'Point out any surface that needs a gentler product before work starts.',
    ],
    faqs: [
      {
        question: 'Do they bring their own supplies?',
        answer: 'Yes. Machines, cloths and eco-friendly agents are all included.',
      },
      ...COMMON_FAQS,
    ],
  },

  plumbing: {
    steps: [
      ARRIVAL,
      {
        title: 'The fault is diagnosed',
        body: 'The professional traces the cause rather than the symptom, and shows you what they found.',
      },
      {
        title: 'You approve before anything is replaced',
        body: 'Parts and cost are quoted up front. No work happens until you say yes.',
      },
      CLOSE,
    ],
    includes: [
      'Full diagnosis and leak tracing',
      'Labour for the repair quoted',
      'Standard sealants, tape and fittings',
      '30-day warranty on the work done',
    ],
    excludes: [
      'Major parts - taps, mixers, motors, cisterns',
      'Wall or floor breaking and re-finishing',
      'Municipal supply or main line faults',
      'Work inside a shared building riser',
    ],
    tips: [
      'Turn off the supply at the stopcock if water is actively running.',
      'Clear the space under the sink so the trap can be reached.',
      'Photograph the leak when it is worst - intermittent faults hide during visits.',
    ],
    faqs: [
      {
        question: 'Are parts included in the price?',
        answer:
          'Consumables are. Larger parts are quoted separately and only fitted once you approve.',
      },
      ...COMMON_FAQS,
    ],
  },

  electrical: {
    steps: [
      ARRIVAL,
      {
        title: 'The circuit is isolated and tested',
        body: 'Power is cut at the board and the line is tested dead before anything is opened.',
      },
      {
        title: 'The fault is repaired and re-tested',
        body: 'The circuit is restored, load-tested, and checked against the rest of the board.',
      },
      CLOSE,
    ],
    includes: [
      'Isolation, testing and safe re-energising',
      'Labour for the repair or installation',
      'Standard wire, connectors and tape',
      '30-day warranty on the work done',
    ],
    excludes: [
      'Fittings - lights, fans, switchboards, appliances',
      'Full rewiring or new circuit runs',
      'Meter or utility-side work',
      'Chasing walls and making good',
    ],
    tips: [
      'Note which sockets or lights fail together - it narrows the circuit immediately.',
      'Keep the consumer unit accessible; boxed-in boards slow the visit down.',
      'Do not reset a breaker repeatedly if it keeps tripping.',
    ],
    faqs: [
      {
        question: 'Is the professional licensed?',
        answer:
          'Yes. Every electrician is licensed and background-checked before they are visible on Homingo.',
      },
      ...COMMON_FAQS,
    ],
  },

  appliance: {
    steps: [
      ARRIVAL,
      {
        title: 'The appliance is inspected',
        body: 'The unit is opened, tested through its cycle, and the failing component identified.',
      },
      {
        title: 'Repair is quoted, then carried out',
        body: 'You see the part and the price before it is fitted. Genuine parts only.',
      },
      CLOSE,
    ],
    includes: [
      'Full diagnosis and cycle testing',
      'Labour for the repair quoted',
      'Cleaning of filters and accessible internals',
      '30-day warranty on the work done',
    ],
    excludes: [
      'Replacement parts - compressors, boards, motors, drums',
      'Gas charging unless stated in the service',
      'Uninstalling or moving built-in units',
      'Faults covered by an active manufacturer warranty',
    ],
    tips: [
      'Have the model number ready - it is on a sticker inside the door or behind the unit.',
      'Clear 60cm around the appliance so it can be pulled out safely.',
      'Note the exact error code if the display shows one.',
    ],
    faqs: [
      {
        question: 'What if it cannot be repaired?',
        answer:
          'You pay the visit and diagnosis only, and get a written explanation of why.',
      },
      ...COMMON_FAQS,
    ],
  },

  carpentry: {
    steps: [
      ARRIVAL,
      {
        title: 'The piece is measured and assessed',
        body: 'The professional checks the material, the fixings and whether a repair will hold.',
      },
      {
        title: 'The work is done in place',
        body: 'Cutting, planing and fixing are done on site with dust sheets down.',
      },
      CLOSE,
    ],
    includes: [
      'Measurement and assessment',
      'Labour for the work quoted',
      'Standard screws, hinges and adhesive',
      'Clean-up of shavings and dust',
    ],
    excludes: [
      'Timber, boards, laminate and hardware',
      'Polishing, painting or staining',
      'Full furniture fabrication',
      'Structural or load-bearing changes',
    ],
    tips: [
      'Empty the cupboard or wardrobe before the slot.',
      'Keep a sample of the finish if you want a close match.',
      'Say up front whether you want it repaired or replaced.',
    ],
    faqs: [
      {
        question: 'Do you supply the wood?',
        answer:
          'No - material is quoted separately, or you can supply your own.',
      },
      ...COMMON_FAQS,
    ],
  },

  pest: {
    steps: [
      ARRIVAL,
      {
        title: 'The infestation is traced',
        body: 'Entry points, nests and harbourage areas are found before anything is sprayed.',
      },
      {
        title: 'Targeted treatment is applied',
        body: 'Gel, spray or bait as the case needs - applied to the source, not the whole house.',
      },
      CLOSE,
    ],
    includes: [
      'Full inspection and identification',
      'Odourless, child- and pet-safe chemicals',
      'Treatment of entry points and harbourage',
      'Warranty period stated at booking',
    ],
    excludes: [
      'Structural repair of entry points',
      'Termite pre-construction treatment',
      'Removal of large animals or bee hives',
      'Deep cleaning after treatment',
    ],
    tips: [
      'Do not clean treated surfaces for the period the professional specifies.',
      'Store open food and cover aquariums before the slot.',
      'Plan to be out of the treated rooms for the stated hours.',
    ],
    faqs: [
      {
        question: 'Is it safe for children and pets?',
        answer:
          'Yes. The chemicals are odourless and certified safe once the stated re-entry time has passed.',
      },
      ...COMMON_FAQS,
    ],
  },

  beauty: {
    steps: [
      ARRIVAL,
      {
        title: 'A clean single-use kit is opened',
        body: 'Disposables are unsealed in front of you and tools are sanitised before the service.',
      },
      {
        title: 'The service is carried out',
        body: 'Products are shown before use so you can flag an allergy or a preference.',
      },
      CLOSE,
    ],
    includes: [
      'All products and single-use disposables',
      'Setup and full clean-up afterwards',
      'A trained, background-checked professional',
      'Sanitised tools, opened in front of you',
    ],
    excludes: [
      'Treatments not listed in this service',
      'Products you ask to be used but did not supply',
      'Medical or dermatological procedures',
      'Services for anyone under 16 without a guardian present',
    ],
    tips: [
      'Have a chair near a socket and good light ready.',
      'Mention allergies and sensitivities when you book, not on the day.',
      'Skip other products on the area for 24 hours beforehand.',
    ],
    faqs: [
      {
        question: 'Are the tools sterilised?',
        answer:
          'Every tool is sanitised between clients and disposables are opened in front of you.',
      },
      ...COMMON_FAQS,
    ],
  },

  wellness: {
    steps: [
      ARRIVAL,
      {
        title: 'The space is set up',
        body: 'Table, linens and oils are brought and laid out; you choose pressure and focus areas.',
      },
      {
        title: 'The session runs to the full time',
        body: 'Setup and pack-down are not counted - the stated duration is hands-on time.',
      },
      CLOSE,
    ],
    includes: [
      'Portable table, fresh linens and oils',
      'Full stated duration of hands-on time',
      'Setup and pack-down',
      'A certified therapist',
    ],
    excludes: [
      'Physiotherapy or medical treatment',
      'Sessions for anyone with an untreated injury',
      'Extra time beyond the booked duration',
      'Use of your own oils unless agreed',
    ],
    tips: [
      'Clear a 2m × 2m space for the table.',
      'Avoid a heavy meal in the hour before.',
      'Say early if you want lighter pressure - mid-session is late to adjust.',
    ],
    faqs: [
      {
        question: 'Do they bring a massage table?',
        answer: 'Yes, along with fresh linens. You only need to clear the floor space.',
      },
      ...COMMON_FAQS,
    ],
  },
};

// ------------------------------------------------------------------ reviews

const REVIEWERS = [
  'Priya S.', 'Rahul M.', 'Ananya K.', 'Vikram T.', 'Meera J.',
  'Arjun D.', 'Sneha R.', 'Kabir N.', 'Divya P.', 'Rohan G.',
  'Ishita B.', 'Aditya V.',
];

const PRAISE = [
  'Turned up exactly on time and worked without any fuss. Would book again.',
  'Very professional and detail-oriented. Explained everything before starting.',
  'Left the place cleaner than they found it. No mess at all.',
  'Booked in the morning, done by afternoon. Price was exactly as quoted.',
  'Polite, careful with our things, and clearly knew the job well.',
  'Second time booking the same person. Consistent every visit.',
  'Quick, quiet and thorough. Nothing to complain about.',
  'Answered all my questions patiently and did not rush the work.',
];

const MIXED = [
  'Good work overall, though they arrived toward the end of the slot.',
  'Job was done properly, but took a little longer than estimated.',
];

const WHEN = ['2 days ago', '1 week ago', '2 weeks ago', '3 weeks ago', '1 month ago', '2 months ago'];

/** Stable small hash of the service key, so nothing reshuffles between renders. */
const seedOf = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) % 100000;
  }
  return hash;
};

/**
 * Reviews are derived from the rating: a 4.9 service shows almost only fives,
 * a 4.6 shows a couple of fours. Building them from the number the card
 * already displays keeps the two from contradicting each other.
 */
const reviewsFor = (service: Service): Review[] => {
  /*
   * An unrated service has nothing to shape reviews from, and inventing them
   * would be worse here than anywhere else on the screen - these are quotes
   * with names on them. Empty, and the sheet drops the section.
   */
  if (service.rating === undefined) {
    return [];
  }

  const seed = seedOf(service.key);
  const count = 8 + (seed % 3); // 8-10, enough for "show more" to mean something
  const softCount = service.rating >= 4.9 ? 1 : service.rating >= 4.8 ? 2 : 3;

  return Array.from({ length: count }, (_, i) => {
    const soft = i >= count - softCount;
    return {
      name: REVIEWERS[(seed + i * 5) % REVIEWERS.length],
      rating: soft ? 4 : 5,
      when: WHEN[(seed + i * 3) % WHEN.length],
      body: soft
        ? MIXED[(seed + i) % MIXED.length]
        : PRAISE[(seed + i * 7) % PRAISE.length],
    };
  });
};

/**
 * Star distribution shaped to the service's own average, so the bars and the
 * headline number agree. Fives dominate, and the tail grows as the average
 * drops.
 */
const breakdownFor = (service: Service): Breakdown => {
  if (service.rating === undefined) {
    return [0, 0, 0, 0, 0];
  }

  const seed = seedOf(service.key);
  const above = Math.max(0, Math.min(1, (service.rating - 4.4) / 0.6));
  const five = Math.round(52 + above * 40) + (seed % 5);
  const four = Math.round(30 - above * 20);
  const three = Math.round(10 - above * 7);
  const two = Math.round(5 - above * 4);
  const one = Math.max(1, Math.round(4 - above * 3));
  return [five, four, three, two, one];
};

// ------------------------------------------------------------------ banners

/**
 * The service's own photograph first, then its siblings in the same category.
 *
 * Reusing the catalogue's images avoids a second static `require` map - Metro
 * resolves `require` at build time, so a folder of tiles cannot be indexed
 * dynamically anyway.
 */
const bannersFor = (
  service: Service,
  category?: Category,
): ImageSourcePropType[] => {
  /*
   * ---------------------------------------------------------------------
   * THE PLATFORM'S OWN PICTURES, IN ORDER OF HOW SPECIFIC THEY ARE
   * ---------------------------------------------------------------------
   * The service's photograph, then its category's. Both are uploaded in the
   * console and reach the app as URLs - `ApiService.imageUrl` and
   * `ApiCategoryNode.iconUrl` - and every live service and category has one.
   *
   * This used to top the carousel up with the BUNDLED artwork of whatever
   * trade the slug mapped to, on the reasoning that a photograph of a plumber
   * is true of any plumbing job. That was fine while the app had pictures and
   * the platform did not. The platform has them now, and the borrowed ones
   * were being shown beside a real service's name and price as though they
   * were pictures of it.
   *
   * Nothing is borrowed any more. A service with only its own photograph gets
   * a carousel of one, which is honest and looks like a photograph rather than
   * like a gallery of somebody else's work.
   */
  const seen = new Set<ImageSourcePropType>();
  const banners: ImageSourcePropType[] = [];

  [service.image, category?.image].forEach(image => {
    if (image && !seen.has(image)) {
      seen.add(image);
      banners.push(image);
    }
  });

  return banners.slice(0, 4);
};

/**
 * Other services in the same category, for the "similar" rail.
 *
 * ---------------------------------------------------------------------------
 * EMPTY FOR A SERVICE THAT CAME FROM THE PLATFORM
 * ---------------------------------------------------------------------------
 * Deliberately, and it is the more important half of this file's live-catalogue
 * handling. Filling the rail from the seed would put cards on screen carrying
 * seed keys and seed prices - and every one of them is tappable, addable to the
 * basket and then sent to a checkout that would be quoting a service the server
 * has never heard of at a price nobody set.
 *
 * A missing rail is a smaller loss than a bookable ghost. The sheet already
 * hides the section when this is empty. When similar services are worth having
 * back, they should come from the live catalogue the screen already holds, not
 * from here.
 */
export const similarTo = (service: Service): Service[] => {
  const isSeeded = services.some(s => s.key === service.key);
  if (!isSeeded) {
    return [];
  }

  return services
    .filter(s => s.category === service.category && s.key !== service.key)
    .slice(0, 6);
};

export const detailFor = (
  service: Service,
  /* The service's own category, for its photograph. Optional so the callers
     that only have a service - tests, and anything reading the scope lists -
     do not have to look one up to get them. */
  category?: Category,
): ServiceDetail => {
  /*
   * The template for the trade this category resembles.
   *
   * `TEMPLATES` is keyed by the eight categories this app was written around,
   * and a service's category is now whatever the platform calls it -
   * "plumbing-repairs", "ac-service". Indexing straight into it returned
   * `undefined` and the next line threw, which is what broke every service
   * detail sheet the moment the catalogue went live.
   *
   * `tradeOf` maps a server slug back to one of the eight; `cleaning` is the
   * fallback for a category it cannot place at all. That is a guess, and it is
   * the right kind of guess: generic "how it works" copy under a real name and
   * price is a page somebody can still book from, whereas no page at all is a
   * dead end.
   */
  const template = TEMPLATES[tradeOf(service.category) ?? 'cleaning'];
  return {
    banners: bannersFor(service, category),
    /*
     * ---------------------------------------------------------------------
     * THE PLATFORM'S OWN WORDS, OR NOTHING
     * ---------------------------------------------------------------------
     * These three used to fall back to the template when ops had not written
     * them. That was the reasonable-looking choice and it is the wrong one: a
     * service with an empty `included` column then showed a generic list of
     * the trade's inclusions, under this service's real name and real price,
     * with nothing to say nobody had written it. A customer reads a promise
     * that no Pro has been told to keep.
     *
     * Measured before removing it: nine of eleven live services have both
     * columns filled, so this was showing invented copy on two - "Hourly
     * Services" (nothing at all, no FAQs either) and "Wash Area Cleaning".
     *
     * Empty is the honest answer, and the screen now hides the card rather
     * than drawing an empty heading. If ops leaves a service blank its page
     * is thinner; that is a gap somebody can see and fix, which is more than
     * the old behaviour offered.
     *
     * `steps` and `tips` below are NOT in this rule and stay the template's:
     * they describe the platform's own process, are identical for every
     * service, and neither is drawn on this screen today anyway.
     */
    includes: service.includes ?? [],
    excludes: service.excludes ?? [],
    /*
     * `steps` and `tips` are the exception, and deliberately so.
     *
     * Neither is a claim about THIS service. They describe the platform's own
     * process - a vetted professional arrives, the work is checked, you
     * approve it - which is true of every service and is not eleven copies
     * anybody should be maintaining. There is no server column for either,
     * and inventing one to hold the same paragraph eleven times would be
     * work with no reader.
     *
     * Both are also unrendered on the detail screen today; they are kept
     * populated because `tips` belongs on the booking confirmation and
     * putting the "How it works" band back is uncommenting a block.
     */
    steps: template.steps,
    tips: template.tips,
    faqs: service.faqs ?? [],
    breakdown: breakdownFor(service),
    reviews: reviewsFor(service),
  };
};
